import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import useTranslate from 'common/I18n/Interpreter';
import { Icon, Label } from '@atlas/ui';
import ErrorBlock from 'common/Input/ErrorBlock';

const Input = ({
  type = 'text',
  label = null,
  name,
  autoFocus = false,
  onFocus,
  onBlur,
  onChange,
  placeholder = '',
  touched = false,
  active = false,
  disabled = false,
  className = '',
  error = '',
  errors = [],
  rules = [],
  value,
  withTranslate = true,
  leftContent = null,
  isRequiredSymbol = false,
}) => {
  const [isShowing, setIsShowing] = useState(false);

  const t = useTranslate();

  const inputType = useMemo(() => {
    if (type !== 'password') {
      return type;
    }

    return isShowing ? 'text' : 'password';
  }, [isShowing]);

  const renderValidationSymbol = useMemo(() => {
    if (!touched) {
      return;
    }

    const isInvalid = error || (errors && errors.length > 0);
    return (
      isInvalid && (
        <div className="validation-symbol">
          <Icon icon="icon-validation-fail" className="validation-symbol-icon" />
        </div>
      )
    );
  }, [touched, error, errors]);
  const renderShowSymbol = useMemo(() => {
    if (type !== 'password') {
      return null;
    }

    const showSymbolClassName = cx('show-symbol', isShowing && 'show-symbol-active');

    return (
      <div className={showSymbolClassName} onClick={() => setIsShowing(!isShowing)}>
        <Icon icon="icon-show-password" className="show-symbol-icon" />
      </div>
    );
  }, [isShowing, setIsShowing]);

  const renderLabel = useMemo(
    () =>
      label ? (
        <label htmlFor={name} className="label">
          {withTranslate ? <Label message={label} /> : label}
          {isRequiredSymbol && <span className="required"> *</span>}
        </label>
      ) : null,
    [label, withTranslate],
  );

  const fieldClassName = cx(
    'field',
    touched && 'field-touched',
    error && 'field-error',
    active && 'field-active',
    disabled && 'field-disabled',
    className,
  );
  const inputClassName = cx('input', `input-${type}`);

  return (
    <Label
      render={() => (
        <div className={fieldClassName}>
          {renderLabel}
          <div className="input-container">
            <input
              value={value}
              type={inputType}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              id={name}
              name={name}
              className={inputClassName}
              placeholder={withTranslate ? t(placeholder) : placeholder}
              autoFocus={autoFocus}
              disabled={disabled}
            />
            {leftContent}
            {renderValidationSymbol}
            {renderShowSymbol}
          </div>
          <ErrorBlock rules={rules} errors={errors} error={error} withTranslate={withTranslate} />
        </div>
      )}
    />
  );
};

Input.propTypes = {
  type: PropTypes.oneOf(['text', 'password', 'date']),
  value: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  touched: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  isRequiredSymbol: PropTypes.bool,
  errors: PropTypes.array,
  rules: PropTypes.array,
  withTranslate: PropTypes.bool,
  leftContent: PropTypes.node,
};

export default Input;
